<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-6 max-w-[1500px] mx-auto">

    <!-- Page header -->
    <div class="flex flex-row items-end justify-between gap-4 mb-5 flex-wrap">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Stock Scrap</h1>
        <p class="text-sm text-gray-500 mt-1">Damaged, lost or rejected stock written off - logging one takes it out of the yard's count straight away</p>
      </div>
      <button
        class="group flex items-center gap-2.5 pl-2.5 pr-5 py-2 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-md shadow-amber-200 hover:shadow-lg hover:shadow-amber-300 hover:from-amber-300 hover:to-amber-400 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all focus:outline-none focus:ring-4 focus:ring-amber-200"
        (click)="onCreateScrap()">
        <span class="h-8 w-8 rounded-lg bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform group-hover:rotate-90 duration-200">
          <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
        </span>
        Log scrap
      </button>
    </div>

    <!-- Headline figures -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
        <span class="h-11 w-11 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center"><mat-icon>delete_forever</mat-icon></span>
        <div><div class="text-2xl font-bold text-gray-900 leading-none">{{ stats?.total ?? '–' }}</div><div class="text-xs text-gray-500 mt-1">Entries logged</div></div>
      </div>
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
        <span class="h-11 w-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon>inventory_2</mat-icon></span>
        <div><div class="text-2xl font-bold text-gray-900 leading-none">{{ stats?.units ?? '–' }}</div><div class="text-xs text-gray-500 mt-1">Units written off</div></div>
      </div>
      <button class="text-left bg-white rounded-2xl border shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-all" [class]="source === 'auto' ? 'border-sky-300 ring-2 ring-sky-100' : 'border-gray-100'" (click)="setSource('auto')">
        <span class="h-11 w-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center"><mat-icon>fact_check</mat-icon></span>
        <div><div class="text-2xl font-bold text-gray-900 leading-none">{{ stats?.auto ?? '–' }}</div><div class="text-xs text-gray-500 mt-1">Found during delivery checks</div></div>
      </button>
      <button class="text-left bg-white rounded-2xl border shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-all" [class]="source === 'manual' ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-gray-100'" (click)="setSource('manual')">
        <span class="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><mat-icon>edit_note</mat-icon></span>
        <div><div class="text-2xl font-bold text-gray-900 leading-none">{{ stats?.manual ?? '–' }}</div><div class="text-xs text-gray-500 mt-1">Logged by hand</div></div>
      </button>
    </div>

    @if (loadError) {
      <div class="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm flex items-center justify-between">
        <span>Could not load the scrap log from the server.</span>
        <button class="text-xs uppercase font-semibold underline" (click)="load()">Try again</button>
      </div>
    }

    <!-- Search + filters -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 mb-5">
      <div class="flex flex-col lg:flex-row lg:items-center gap-3">
        <div class="relative flex-1">
          <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
          <input type="text" placeholder="Search by product name or SKU"
            class="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
            name="scrap-search" [(ngModel)]="keyword" (ngModelChange)="onSearchInput()" />
          @if (keyword) {
          <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="clearSearch()" aria-label="Clear search">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
          </button>
          }
        </div>
        <select class="px-3 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 outline-none text-sm" [(ngModel)]="warehouse" (ngModelChange)="applyFilters()" aria-label="Yard">
          @for (w of warehouseOptions; track w) { <option [value]="w">{{ w === 'ALL' ? 'Both yards' : w }}</option> }
        </select>
        <select class="px-3 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 outline-none text-sm" [(ngModel)]="reason" (ngModelChange)="applyFilters()" aria-label="Reason">
          @for (r of reasonOptions; track r.value) { <option [value]="r.value">{{ r.label }}</option> }
        </select>
      </div>
      <div class="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 flex-wrap">
        @for (o of sourceOptions; track o.value) {
        <button class="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all"
          [class]="source === o.value ? 'bg-neutral-900 text-white border-neutral-900 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50'"
          (click)="setSource(o.value)">
          {{ o.label }}
        </button>
        }
        @if (filtersActive) {
        <button class="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600 px-2 py-1.5 ml-auto" (click)="resetFilters()">
          <mat-icon class="!text-[16px] !h-4 !w-4">filter_alt_off</mat-icon> Clear all
        </button>
        }
      </div>
    </div>

    @if (loading && scrapEntries.length === 0) {
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm min-h-80 flex flex-col justify-center items-center">
        <mat-spinner diameter="32"></mat-spinner>
        <div class="text-sm text-gray-500 mt-3">Loading the scrap log&hellip;</div>
      </div>
    } @else if (scrapEntries.length === 0) {
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm min-h-80 flex flex-col justify-center items-center text-center">
        <span class="h-14 w-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3"><mat-icon class="!text-amber-500 !text-[30px] !h-[30px] !w-[30px]">delete_forever</mat-icon></span>
        <div class="text-lg font-semibold text-gray-800">{{ filtersActive ? 'No entries match these filters' : 'No stock has been scrapped yet' }}</div>
        <div class="text-sm text-gray-500 mt-1">{{ filtersActive ? 'Try a different search or clear the filters' : 'Damaged or lost stock found on the shelf can be logged with "Log scrap"' }}</div>
        @if (filtersActive) { <button class="mt-4 text-sm font-semibold text-amber-700 hover:underline" (click)="resetFilters()">Clear all filters</button> }
      </div>
    } @else {
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-opacity" [class.opacity-60]="loading">
        @for (row of scrapEntries; track row.id) {
        <div class="border-b border-gray-50 last:border-b-0 hover:bg-amber-50/30 transition-colors">
          <div class="flex items-center gap-4 px-5 py-4">

            <span class="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0" [class]="reasonStyle(row.reason).cls"><mat-icon class="!text-[20px] !h-5 !w-5">{{ reasonStyle(row.reason).icon }}</mat-icon></span>

            <!-- What -->
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-semibold text-gray-900 truncate">{{ row.productName || 'Variant #' + row.productVariantId }}</span>
                <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full" [class]="reasonStyle(row.reason).cls">{{ reasonStyle(row.reason).label }}</span>
                @if (row.auto) { <span class="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-sky-50 text-sky-700" matTooltip="Written automatically when this was found damaged during a delivery check"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">fact_check</mat-icon>Delivery check</span> }
              </div>
              <div class="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                @if (row.sku) { <span class="font-mono">{{ row.sku }}</span> }
                @if (row.orderNo) { <span>Order #{{ row.orderNo }}</span> }
              </div>
              @if (cleanNotes(row)) { <div class="mt-1.5 text-xs text-gray-500 bg-gray-50 rounded-lg px-2.5 py-1 inline-block max-w-full truncate" [matTooltip]="cleanNotes(row)">{{ cleanNotes(row) }}</div> }
            </div>

            <!-- Qty / yard -->
            <div class="w-32 flex-shrink-0 text-right hidden md:block">
              <div class="text-lg font-bold text-gray-900">−{{ row.quantity }}</div>
              <div class="text-[11px] text-gray-400">{{ row.warehouse }}</div>
            </div>

            <!-- Who / when -->
            <div class="w-36 flex-shrink-0 hidden lg:block text-right">
              <div class="text-xs font-semibold text-gray-800">{{ row.createdAt | date : 'd MMM y' }}</div>
              <div class="text-[11px] text-gray-400 mt-0.5">by {{ loggedByName(row.scrappedBy) }}</div>
            </div>
          </div>
        </div>
        }
        <mat-paginator class="border-t border-gray-100"
          [length]="count" [pageSize]="itemsPerPage" [pageIndex]="pageNumber"
          [pageSizeOptions]="[20, 50, 100]" showFirstLastButtons (page)="updateList($event)">
        </mat-paginator>
      </div>
    }

  </div>
  </div>
</app-main-layout>
